import type { SymptomCode } from "./symptom-entry";

// TES-1442 (S5 of TES-1437) — the structured side-effect details of F-07 v14.
//
// Drive, "Kanoniskt datakontrakt 2026-10-19":
// "F-07 frågar om upplevda besvär kopplade till behandlingen som patienten kommer med. Den tidigare fritextbaserade detaljinsamlingen kompletteras med gemensamma strukturerade besvärsval; befintlig kontexttext bevaras. Tidigare och aktuella besvär hålls isär."
// and "Upplevt samband med läkemedel är patientens uppgift, inte fastställd medicinsk kausalitet."
//
// Built as Ö-786 assumption (a): after "Ja" or "Jag är osäker" on
// `pt_side_effects`, she picks the symptoms from the register (SYMPTOM_TYPE)
// and, per symptom, says when it began (SYMPTOM_STARTED_AT) and may add a
// note (SYMPTOM_NOTE). No "just nu" severity: F-07 does not ask whether the
// side effect is still there, so an answer here is about her earlier
// treatment (SymptomReferencePeriod `before_ella`), never a current point.
//
// The keys live here so the form (api seed), the bridge that writes her
// answers to SymptomEntry and the journal phrasing read the same ones.
// `pt_symptom_<code>_<part>` follows the check-ins' per-symptom keys
// (checkinSymptomFieldKey), with F-07's `pt` prefix.

/** The multi-select of symptoms, bound to SYMPTOM_TYPE. */
export const TRANSFER_SYMPTOM_TYPES_KEY = "pt_symptom_types";

/** The per-symptom questions F-07 asks, and the register question of each. */
export const TRANSFER_SYMPTOM_FIELD_PARTS = {
  started: "SYMPTOM_STARTED_AT",
  note: "SYMPTOM_NOTE",
} as const;
export type TransferSymptomFieldPart =
  keyof typeof TRANSFER_SYMPTOM_FIELD_PARTS;
export const TRANSFER_SYMPTOM_FIELD_PART_KEYS = Object.keys(
  TRANSFER_SYMPTOM_FIELD_PARTS,
) as TransferSymptomFieldPart[];

export function transferSymptomFieldKey(
  code: SymptomCode,
  part: TransferSymptomFieldPart,
): string {
  return `pt_symptom_${code}_${part}`;
}

// ---------------------------------------------------------------------------
// TES-1532 — F-07 v17: her earlier symptoms in F-07's own words, and whether
// each one is still there.
//
// Emelie 2026-10-21, Ö-786: "Per valt historiskt besvär fråga först ”Har du fortfarande det här besväret?” Vid Ja ställs den gemensamma frågan om aktuell svårighetsgrad: ”Hur mycket besvär har du av detta just nu?” med Lätt / Måttligt / Kraftigt. Vid Nej bevaras historik utan aktuell grafpunkt."
// Ö-813: "F-07 får kontextanpassad text: ”Vilka besvär har du haft under din tidigare behandling?” och ”När började besväret?” Därefter den redan godkända frågan ”Har du fortfarande det här besväret?” Samma besvärskoder och historik används. Historisk debut blandas inte med senare förändring i ett otydligt datumfält."
//
// The list and the onset are SYMPTOM_TYPE and SYMPTOM_STARTED_AT asked in
// F-07's decided words: the register's f07 contextCopy, same codes, scale
// and series. Their fields have new keys, so a v14–v16 answer stays labelled
// by the wording it was given to (journal-phrasing). The note keeps v14's key
// and question (SYMPTOM_NOTE, unchanged). "Har du fortfarande det här
// besväret?" is SYMPTOM_STILL_PRESENT; the severity is the shared
// SYMPTOM_SEVERITY, shown only on "Ja".

/** The multi-select of earlier symptoms: SYMPTOM_TYPE in F-07's words. */
export const TRANSFER_EARLIER_SYMPTOM_TYPES_KEY = "pt_earlier_symptom_types";

/** The per-symptom questions F-07 v17 asks, in order, and their register ids. */
export const TRANSFER_EARLIER_SYMPTOM_FIELD_PARTS = {
  onset: "SYMPTOM_STARTED_AT",
  still: "SYMPTOM_STILL_PRESENT",
  severity: "SYMPTOM_SEVERITY",
  note: "SYMPTOM_NOTE",
} as const;
export type TransferEarlierSymptomFieldPart =
  keyof typeof TRANSFER_EARLIER_SYMPTOM_FIELD_PARTS;
export const TRANSFER_EARLIER_SYMPTOM_FIELD_PART_KEYS = Object.keys(
  TRANSFER_EARLIER_SYMPTOM_FIELD_PARTS,
) as TransferEarlierSymptomFieldPart[];

/** `pt_symptom_<code>_<part>`; the note is v14's own key. */
export function transferEarlierSymptomFieldKey(
  code: SymptomCode,
  part: TransferEarlierSymptomFieldPart,
): string {
  return `pt_symptom_${code}_${part}`;
}

/** The answer to SYMPTOM_STILL_PRESENT that asks the severity. */
export const SYMPTOM_STILL_PRESENT_YES = "ja";

// F-07's decided wording (Ö-786, Ö-813), verbatim; the English is ours. The
// register reads it from here: `list` is SYMPTOM_TYPE's and `onset`
// SYMPTOM_STARTED_AT's f07 contextCopy, `still` is SYMPTOM_STILL_PRESENT.
export const TRANSFER_SYMPTOM_QUESTIONS = {
  list: {
    label: {
      sv: "Vilka besvär har du haft under din tidigare behandling?",
      en: "Which symptoms did you have during your earlier treatment?",
    },
  },
  onset: {
    label: {
      sv: "När började besväret?",
      en: "When did the symptom start?",
    },
  },
  still: {
    label: {
      sv: "Har du fortfarande det här besväret?",
      en: "Do you still have this symptom?",
    },
    options: [
      { value: SYMPTOM_STILL_PRESENT_YES, label: { sv: "Ja", en: "Yes" } },
      { value: "nej", label: { sv: "Nej", en: "No" } },
    ],
  },
} as const;
